"use client";

import { motion } from "framer-motion";
import React from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const galleryItems = [{
  id: 1,
  title: "Eingangsbereich.",
  description: "Reinkommen. Durchatmen. Ankommen. Hell, offen und alles auf einer Etage. Kein langer Flur, kein dunkles Treppenhaus, du stehst in der Tür und siehst sofort, wo dein Tag stattfindet.",
  image: "https://perfect-duck.10web.cloud/wp-content/uploads/2026/03/40-Bluvit_CoWorkingSpace_05052025_sandraschumann_0078-scaled.jpg?v=ruw3v5henaj"
}, {
  id: 2,
  title: "Loungebereich.",
  description: "Dein Sofa für zwischendurch.\nManchmal braucht der beste Gedanke kein Schreibtisch, sondern ein Sofa. Lehn dich zurück, nimm dir fünf Minuten oder eine ganze Brainstorming-Session.",
  image: "https://perfect-duck.10web.cloud/wp-content/uploads/2026/03/Loungebereich.jpg?v=5gn097cfrx8"
}, {
  id: 3,
  title: "Schließfächer.",
  description: "Bunt. Sicher. Deins.\nLaptop, Unterlagen, Lieblingskaffeetasse, für alles, was du nicht jeden Tag mitschleppen willst. Abschließen, loslassen, loslegen.",
  image: "https://perfect-duck.10web.cloud/wp-content/uploads/2026/03/106-Bluvit_CoWorkingSpace_05052025_sandraschumann_0009-scaled.jpg?v=v2qqmpuwww"
}, {
  id: 4,
  title: "Schreibtischplatz.",
  description: "Dein Platz. So wie du ihn brauchst.\nErgonomische Stühle, höhenverstellbare Tische und genug Raum für konzentriertes Arbeiten allein oder Seite an Seite mit anderen, wenn Teamwork gefragt ist.",
  image: "https://perfect-duck.10web.cloud/wp-content/uploads/2026/04/2-Bluvit_CoWorkingSpace_05052025_sandraschumann_0116.jpg?v=jgv1dm3342"
}, {
  id: 5,
  title: "Küche.",
  description: "Kaffee geht aufs Haus.\nKaffee, Tee, voll ausgestattete Küche. Kühlschrank, Herd, micro-well, alles da. Hier kochst du dir dein Mittagessen, statt es teuer um die Ecke zu holen.",
  image: "https://perfect-duck.10web.cloud/wp-content/uploads/2026/03/Kuche-scaled.jpg?v=btusri9sa5h"
}, {
  id: 6,
  title: "Billardtisch.",
  description: "Feierabend fängt eine Etage früher an. Kopf voll? Eine Runde Billard löst mehr als jede To-do-Liste. Der perfekte Reset zwischen zwei Meetings oder der beste Grund, nach der Arbeit noch kurz zu bleiben.",
  image: "https://perfect-duck.10web.cloud/wp-content/uploads/2026/04/Billardtisch-scaled.jpg?v=m34wepzq4o"
}];
export default function Gallery() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="gallery" className="bg-[#EEEEEE] ![padding:144px_0px_144px_0px] overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-[#888888] mb-4 block">Galerie</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              <span className="text-[#141414]">Einmal umschauen</span><br />
              <span className="text-[#888888]">und bleiben.</span>
            </h2>
          </div>
          <div className="flex items-end">
            <p className="text-lg text-[#555555] leading-relaxed">
              Helle Räume. Durchdachte Ausstattung. Und das Gefühl, morgens gerne herzukommen. Kein Großraumbüro-Grau, kein Homeoffice-Provisorium — sondern ein Ort, der zum Arbeiten gemacht ist.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryItems.map((item, i) => <GalleryCard key={item.id} item={item} index={i} />)}
        </div>
      </div>
    </section>;
}
function GalleryCard({
  item,
  index
}: {
  item: (typeof galleryItems)[0];
  index: number;
}) {
  const {
    ref: cardRef,
    isVisible: isCardVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
        delay: index * 0.1
      }
    }
  };
  return <motion.div ref={cardRef} data-index={index} initial="hidden" animate={isCardVisible ? "visible" : "hidden"} variants={cardVariants} className="group cursor-default flex flex-col">
      <div className="mb-4 min-h-[140px]">
        <h3 className="text-base font-bold text-[#141414] mb-2">
          {item.title}
        </h3>
        <p className="text-sm text-[#888888] leading-relaxed">
          {item.description}
        </p>
      </div>
      <div className="rounded-[1rem] overflow-hidden flex-1">
        <AspectRatio ratio={3 / 4}>
          <img src={item.image} alt={item.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
        </AspectRatio>
      </div>
    </motion.div>;
}